import { useEffect, useState, useRef } from "react";
import { Check } from "lucide-react";

/**
 * PhoneFrame — scripted animated phone.
 * Plays the animation ONCE on mount. Parent must remount via `key` prop
 * to restart the animation (no autoRestart inside, no replayKey logic).
 *
 * script shape:
 *   header: { title, status }
 *   user: string | null  -> if null, AI message becomes the opener
 *   ai: string | null
 *   statuses: string[]
 *   result: { title, body, meta }
 *   contact?: { name, role?, avatar?, status? }  // optional contextual chip shown above result
 */
export default function PhoneFrame({ script, testIdPrefix = "phone" }) {
  const hasUser = !!script.user;
  const [step, setStep] = useState(hasUser ? 0 : 1);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const timers = [];
    const initialStep = hasUser ? 0 : 1;

    const STEPS = 3 + script.statuses.length + 1;
    const schedule = [380, 950, ...script.statuses.map(() => 720), 700];

    let cum = 0;
    for (let i = initialStep + 1; i <= STEPS; i++) {
      cum += schedule[i - 1];
      timers.push(
        setTimeout(() => {
          if (aliveRef.current) setStep(i);
        }, cum)
      );
    }
    return () => {
      aliveRef.current = false;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = script;
  const totalStatuses = s.statuses.length;
  const statusesVisible = Math.max(0, Math.min(totalStatuses, step - 2));
  const showUser = !!s.user && step >= 1;
  const showAi = !!s.ai && step >= 2;
  const showStatusCard = step >= 3;
  const showResult = step >= 3 + totalStatuses;

  return (
    <div data-testid={testIdPrefix} className="relative mx-auto w-full max-w-[360px]">
      {/* Single soft halo behind — much more discreet */}
      <div className="absolute -inset-8 -z-10 pointer-events-none">
        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-72 h-72 halo-soft opacity-45" />
      </div>

      <div className="relative bg-[#1A1814] p-[5px] rounded-[42px] shadow-[0_30px_60px_-20px_rgba(26,24,20,0.35)]">
        <div className="relative bg-white overflow-hidden rounded-[38px] aspect-[9/19.5] flex flex-col">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-[108px] h-[26px] bg-[#0A0A0A] rounded-full z-20" />

          <div className="pt-3 pb-1.5 px-6 flex items-center justify-between text-[10px] font-mono text-[#6B6862] relative z-10">
            <span>9:41</span>
            <span className="tracking-widest">● ● ●</span>
          </div>

          {/* App header */}
          <div className="px-4 py-2.5 flex items-center gap-3 border-b border-[#F0EBE0]">
            <div className="relative w-9 h-9 rounded-full bg-white flex items-center justify-center overflow-hidden border border-[#F0EBE0]">
              <img src="/img/iko-mark.png" alt="iko" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-sm tracking-tight text-[#1A1814] lowercase">
                {s.header.title}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] pulse-dot" />
                <span className="font-mono text-[9px] uppercase tracking-wider text-[#6B6862]">
                  {s.header.status}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 px-3.5 py-4 space-y-2.5 overflow-hidden bg-[#F4F2EC]">
            {showUser && (
              <div className="flex justify-end opacity-0" style={{ animation: "fadeUp 0.45s ease-out forwards" }}>
                <div className="max-w-[80%] px-3.5 py-2 bg-[#1A1814] text-white text-[13px] leading-snug rounded-2xl rounded-br-md">
                  {s.user}
                </div>
              </div>
            )}
            {showUser && step >= 2 && (
              <div className="flex justify-end -mt-1.5 pr-1 opacity-0" style={{ animation: "fadeUp 0.4s ease-out 0.05s forwards" }}>
                <span className="font-mono text-[8.5px] uppercase tracking-wider text-[#94918A]">Vu</span>
              </div>
            )}
            {showAi && (
              <div className="flex justify-start opacity-0" style={{ animation: "fadeUp 0.45s ease-out forwards" }}>
                <div className="max-w-[82%] px-3.5 py-2 bg-white border border-[#F0EBE0] text-[13px] leading-snug rounded-2xl rounded-bl-md text-[#1A1814]">
                  {s.ai}
                </div>
              </div>
            )}
            {showStatusCard && (
              <div
                className="bg-white border border-[#F0EBE0] p-3 space-y-1.5 rounded-2xl opacity-0"
                style={{ animation: "fadeUp 0.45s ease-out forwards" }}
              >
                {s.statuses.map((statusLabel, i) => {
                  const visible = i < statusesVisible;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-2 text-[12px] transition-all duration-300 ${
                        visible ? "opacity-100 translate-y-0" : "opacity-30 translate-y-0.5"
                      }`}
                    >
                      <div
                        className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                          visible
                            ? "bg-[#1A1814] text-white"
                            : "bg-[#F0EBE0] text-transparent"
                        }`}
                      >
                        <Check size={10} strokeWidth={3} />
                      </div>
                      <span className={visible ? "text-[#1A1814]" : "text-[#94918A]"}>{statusLabel}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {showResult && (
              <div
                className="relative overflow-hidden rounded-2xl p-3.5 opacity-0 border border-[#F0EBE0] bg-white"
                style={{ animation: "fadeUp 0.5s ease-out forwards" }}
                data-testid={`${testIdPrefix}-result`}
              >
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#437DEA]" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B6862]">
                      {s.result.meta || ""}
                    </span>
                  </div>
                  <div className="font-display font-bold text-[15px] tracking-tight text-[#1A1814] mb-1">
                    {s.result.title}
                  </div>
                  <div className="text-[12px] leading-snug text-[#1A1814]">
                    {s.result.body}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-3 py-2.5 border-t border-[#F0EBE0] bg-white flex items-center gap-2">
            <div className="flex-1 h-8 rounded-full bg-[#F4F2EC] flex items-center px-3.5">
              <span className="font-mono text-[10px] text-[#94918A]">…</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#1A1814] flex items-center justify-center">
              <div className="w-2.5 h-2.5 border-2 border-white rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
